import {
  DESIGNER_VIEW_DEFAULTS,
  applyDesignerCommand,
  isEmptyDesign,
  parseDesignerView,
  resolveDesignAreaConfig,
  zoneScene,
  type Color,
  type DesignAreaAnswer,
  type DesignProblem,
  type DesignerDocument,
} from '@pz/scene';
import type { CreateDesigner, DesignerHandle, DesignerOptions } from '../designer-types.js';
import { observeSize, pointerIsCoarse, showToast, watchViewport, type ToastKind } from '../layers.js';
import { createCanvas, type DesignerCanvas } from './canvas.js';
import { el, type DesignerCtx, type PanelDef, type ToolId } from './panels.js';
import type { CreateShell, DesignerLayout, DesignerShell } from './shell-types.js';
import { layoutFor } from './layout.js';
import { createDesktopShell } from './shell-desktop.js';
import { createPhoneShell } from './phone/shell-phone.js';
import { renderArtworkPanel } from './artwork-panel.js';
import { renderImagePanel } from './image-panel.js';
import { renderLayersPanel } from './layers-panel.js';
import { renderTextPanel, addText as addTextLayer } from './text-panel.js';
import { renderElementsPanel } from './elements-panel.js';
import { renderBackgroundPanel } from './background-panel.js';
import { renderTemplatesPanel } from './templates-panel.js';
import { renderStickerPanel, shopperStickerSettings } from './sticker-panel.js';
import { renderAlignmentPanel } from './alignment-panel.js';
import { renderEffectsPanel } from './effects-panel.js';
import { renderShapePanel } from './shape-panel.js';
import { openSettingsDialog } from './bottom-bar.js';
import { startFlow } from './quick-start.js';
import { readableInk } from './contrast.js';
import { applyDocument, designDocument } from './design-file.js';
import { toolFor } from './commands.js';
import { closeOverlays } from './overlay.js';
import { createDesignerStore, emptyAnswer, type DesignerAction, type DesignerState } from './state.js';
import { DESIGNER_STYLES } from './styles.js';
import { isBlocking, problemMessage, resolveTexts } from './texts.js';

export { DESIGNER_STYLES };
export { layoutFor } from './layout.js';
export type { DesignerCtx, PanelDef, ToolId } from './panels.js';

/**
 * `createDesigner` — the shopper's free-form personalizer for one print zone.
 *
 * The module is the seam described in `designer-types.ts` and nothing else: it
 * is handed a zone's rules, the shopper's current design and four services
 * (fonts, uploads, the artwork catalogue, the renderer), and it reports a
 * design back. It never talks to the server, never reads the product
 * definition, and never touches the page outside its host element — which is
 * what lets the same module run inside the widget's full-screen dialog, inside
 * the Studio's preview frame and on a bare harness page.
 *
 * Inside, the split is deliberate. `state.ts` holds every rule (and is the
 * only part that can be tested without a DOM), `canvas.ts` owns the pixels and
 * the gestures, one file per tool owns its controls — and the CHROME is a
 * plug: this file builds the store, the context and the canvas once, then asks
 * a shell to draw itself around them. Desktop gets the reference editor's rail
 * and toolbar; a phone gets a bottom drawer and a property grid; both drive
 * the same commands, so a design made on one is editable on the other.
 */

/**
 * Tags of edits that come from a control the shopper is still holding — a
 * textarea, a slider mid-drag. Rebuilding the panel under those would steal
 * the focus or the thumb; every other change refreshes it.
 */
const LIVE_TAGS = /^(text|size|shadow|bend|tracking|opacity|crop|brightness|contrast|nudge):/;

/** Layers copied with Ctrl+C, shared by every designer on the page. */
let designerClipboard: DesignerDocument | undefined;

export const createDesigner: CreateDesigner = (options: DesignerOptions): DesignerHandle => {
  const texts = resolveTexts(options.texts);
  const cfg = resolveDesignAreaConfig(options.config);
  const fontIds = new Set(options.fonts.list.map((font) => font.id));

  const root = el('div', 'pzd');
  root.tabIndex = -1;
  root.setAttribute('role', 'group');
  root.setAttribute('aria-label', texts.designerLabel);

  // ── State ────────────────────────────────────────────────────────────────
  let added = 0;
  let destroyed = false;
  const hidden = new Set<string>();
  let problemCache: { answer: DesignAreaAnswer; problems: DesignProblem[] } | undefined;
  const savedListeners = new Set<(saved: boolean) => void>();
  let savedTimer: number | undefined;

  /**
   * The sticker contour the canvas traced last, keyed by the design it was
   * traced for. The validator's hook is synchronous and the tracing is not, so
   * the canvas records its verdict here and the next validation picks it up —
   * which is why a stale verdict can never be reported against a design it was
   * not measured on.
   *
   * By CONTENT, not by object identity: the canvas re-fits a text box to its
   * ink after rendering it, which replaces the answer object without changing
   * anything the contour depends on, and an identity check would throw the
   * verdict away every time that happened.
   */
  let contour: { key: string; verdict: { touchesEdge: boolean; islands: number } } | undefined;
  const contourKey = (answer: DesignAreaAnswer): string => JSON.stringify(answer);

  const store = createDesignerStore({
    config: options.config,
    value: options.value ?? (options.config.sticker?.required
      ? { v: 1, layers: [], sticker: shopperStickerSettings(options.config) }
      : null),
    fontIds,
    debounceMs: 180,
    // The zone's own starting composition is part of the product; only what
    // the shopper added is charged for.
    ...(options.authoredLayerIds ? { authoredLayerIds: options.authoredLayerIds } : {}),
    stickerContour: (answer) => (contour && contour.key === contourKey(answer) ? contour.verdict : undefined),
    onChange: (value, meta) => options.onChange(value, meta),
  });

  // Upgrade only the editable shopper draft. Stored order scenes keep legacy
  // omitted imageBorder semantics and therefore keep their original cut shape.
  if (store.value().sticker?.imageBorder === undefined && store.value().sticker) {
    store.dispatch({ type: 'set', answer: applyDesignerCommand(options.config, store.value(), { op: 'sticker', settings: { ...store.value().sticker!, imageBorder: options.config.sticker?.defaults?.imageBorder ?? false } }), history: false });
  }

  function problems(): DesignProblem[] {
    const answer = store.value();
    if (problemCache?.answer === answer) return problemCache.problems;
    const list = store.meta().problems;
    problemCache = { answer, problems: list };
    return list;
  }

  /** Ink that reads on the product behind the zone; see `contrast.ts`. */
  function inkOnProduct(): Color | undefined {
    // A sticker prints on its own white backing: the default ink is right.
    if (store.value().sticker) return undefined;
    const rgb = canvas.productColor();
    return rgb ? readableInk(rgb, cfg.text.palette) : undefined;
  }

  function dispatch(action: DesignerAction): void {
    if (destroyed) return;
    // New words on a dark shirt are written in a colour that shows up on it —
    // but only when they carry the default colour, never a chosen one.
    if (action.type === 'add' && action.layer.kind === 'text' && action.layer.color.hex.toLowerCase() === cfg.text.palette[0]?.hex.toLowerCase()) {
      const ink = inkOnProduct();
      if (ink) action = { ...action, layer: { ...action.layer, color: ink } };
    }
    if (action.type === 'add') {
      added += 1;
      if (action.select !== false) { ctx.selection.clear(); ctx.selection.add(action.layer.id); }
    }
    if (action.type === 'select') {
      if (!action.id) ctx.selection.clear();
      else if (!ctx.selection.has(action.id)) { ctx.selection.clear(); ctx.selection.add(action.id); }
    }
    store.dispatch(action);
  }

  /**
   * "Sačuvano": the host is told about every change after a short debounce, so
   * the indicator follows the same clock — pending while the shopper is still
   * moving something, settled 600 ms after they stop.
   */
  function markDirty(): void {
    for (const listener of savedListeners) listener(false);
    window.clearTimeout(savedTimer);
    savedTimer = window.setTimeout(() => {
      for (const listener of savedListeners) listener(true);
    }, 600);
  }

  // ── Layout ───────────────────────────────────────────────────────────────
  const coarse = options.coarse ?? pointerIsCoarse();
  root.classList.toggle('is-touch', coarse);
  let layout: DesignerLayout = layoutFor(
    options.layout,
    root.clientWidth || options.host.clientWidth || 0,
    coarse,
  );

  // The shell is declared here and built further down: a shell adjusts the
  // view while it mounts (the phone one switches rulers and the mini toolbar
  // off), and that goes through `setView`, which must not find an
  // uninitialised binding. Until `buildShell` returns, every shell call is a
  // no-op — the freshly built shell syncs itself once it exists.
  let shell: DesignerShell | undefined;

  // ── Context handed to every part ─────────────────────────────────────────
  const ctx: DesignerCtx = {
    view: { ...DESIGNER_VIEW_DEFAULTS },
    setView: (patch) => {
      if (patch.zoom === 1) canvas.resetPan();
      Object.assign(ctx.view, parseDesignerView({ ...ctx.view, ...patch }));
      root.classList.toggle('is-review', ctx.view.review);
      root.classList.toggle('is-dark-workspace', ctx.view.darkMode);
      if (ctx.view.review) shell?.close();
      shell?.sync();
      canvas.relayout();
    },
    copySelection: () => {
      designerClipboard = designDocument(ctx, true);
      ctx.toast(designerClipboard.answer.layers.length ? texts.copiedLayers : texts.copyNothing, 'info');
    },
    pasteSelection: () => {
      if (designerClipboard) applyDocument(ctx, designerClipboard, 'append', 'physical');
      else ctx.toast(texts.pasteNothing, 'info');
    },
    share: options.share ? () => options.share!(store.value()) : undefined,
    ...(options.library
      ? {
        library: {
          ...options.library,
          // The live design, flushed: a save made mid-edit keeps the last edit.
          save: (value, name) => {
            store.flush();
            return options.library!.save(store.value() ?? value, name);
          },
        },
      }
      : {}),
    selection: new Set<string>(),
    root,
    host: options.host,
    rawConfig: options.config,
    config: cfg,
    texts,
    fonts: options.fonts,
    artwork: options.artwork,
    uploads: options.uploads,
    renderer: options.renderer,
    currency: options.currency,
    ...(options.productBackdrop ? { productBackdrop: options.productBackdrop } : {}),
    ...(options.printLocations && options.printLocations.length > 1 && options.switchPrintLocation
      ? {
        printLocations: options.printLocations,
        switchPrintLocation: (id: string) => {
          if (options.printLocations?.find((location) => location.id === id)?.current) return;
          store.flush();
          options.switchPrintLocation!(id, store.value());
        },
      }
      : {}),
    store,
    phone: () => layout === 'phone',
    selected: () => {
      const { answer, selectedId } = store.state();
      return answer.layers.find((layer) => layer.id === selectedId) ?? null;
    },
    dispatch,
    toast: (message: string, kind: ToastKind = 'info') => {
      showToast(options.host, { message, kind });
    },
    openTool: (tool) => shell?.open(tool),
    closeTool: () => shell?.close(),
    refreshPanel: () => shell?.refresh(),
    invalidate: (layerId) => canvas.invalidate(layerId),
    isHidden: (layerId) => hidden.has(layerId),
    setHidden: (layerId, on) => {
      if (on) hidden.add(layerId);
      else hidden.delete(layerId);
      canvas.sync();
      shell?.refresh();
    },
    addCount: () => added,
    problemsFor: (layerId) => problems().filter((problem) => problem.layerId === layerId),
    problems,
    startFlow: (kind) => { void startFlow(ctx, kind); },
    inkOnProduct,
    productRgb: () => canvas.productColor(),
  };

  // ── Panels, in rail order ────────────────────────────────────────────────
  const defs: PanelDef[] = [
    { id: 'templates', label: texts.toolTemplates, icon: 'template', allowed: true, render: renderTemplatesPanel },
    { id: 'text', label: texts.addText, icon: 'text', allowed: cfg.allow.text, render: renderTextPanel },
    { id: 'image', label: texts.toolImages, icon: 'image', allowed: cfg.allow.image, render: renderImagePanel },
    { id: 'background', label: texts.toolBackground, icon: 'opacity', allowed: cfg.allow.background, render: renderBackgroundPanel },
    {
      id: 'elements',
      label: texts.toolElements,
      icon: 'shape',
      allowed: cfg.allow.shape || cfg.allow.qr,
      render: renderElementsPanel,
    },
    {
      id: 'icons',
      label: texts.toolIcons,
      icon: 'artwork',
      allowed: cfg.allow.artwork || cfg.allow.emoji,
      render: (body, context) => renderArtworkPanel(body, context, 'icons'),
    },
    { id: 'layers', label: texts.layers, icon: 'layers', allowed: true, render: renderLayersPanel },
    {
      id: 'sticker',
      label: texts.toolSticker,
      icon: 'sticker',
      allowed: options.config.sticker?.allowed === true,
      render: renderStickerPanel,
    },
    // Opened from the contextual toolbar, not from the rail.
    { id: 'alignment', label: texts.alignmentTool, icon: 'alignCenter', allowed: true, rail: false, render: renderAlignmentPanel },
    { id: 'effects', label: texts.effectsTool, icon: 'magic', allowed: true, rail: false, render: renderEffectsPanel },
    // Aliases the phone drawer and older hosts open by name.
    { id: 'artwork', label: texts.addArtwork, icon: 'artwork', allowed: cfg.allow.artwork, rail: false, render: (body, context) => renderArtworkPanel(body, context, 'artwork') },
    { id: 'emoji', label: texts.addEmoji, icon: 'emoji', allowed: cfg.allow.emoji, rail: false, render: (body, context) => renderArtworkPanel(body, context, 'emoji') },
    { id: 'shape', label: texts.addShape, icon: 'shape', allowed: cfg.allow.shape, rail: false, render: renderShapePanel },
    { id: 'arrange', label: texts.alignmentTool, icon: 'alignCenter', allowed: true, rail: false, render: renderAlignmentPanel },
    { id: 'qr', label: 'QR', icon: 'qr', allowed: cfg.allow.qr, rail: false, render: renderElementsPanel },
    { id: 'pattern', label: texts.tiling, icon: 'copy', allowed: cfg.allow.pattern, rail: false, render: renderEffectsPanel },
    {
      id: 'settings',
      label: texts.settings,
      icon: 'settings',
      allowed: true,
      rail: false,
      render: (_body, context) => {
        openSettingsDialog(context);
      },
    },
    {
      id: 'files',
      label: texts.fileSection,
      icon: 'upload',
      allowed: true,
      rail: false,
      render: (_body, context) => {
        openSettingsDialog(context);
      },
    },
  ];

  const canvas: DesignerCanvas = createCanvas(ctx, {
    onOpenLayer: (layer) => shell?.open(toolFor(layer)),
    onAddText: (text) => addTextLayer(ctx, text),
    onStickerContour: (answer, verdict) => {
      contour = { key: contourKey(answer), verdict };
      // The verdict arrived after the design was validated, so the cached
      // problem list is now one code short.
      problemCache = undefined;
      shell?.sync();
      shell?.refresh();
    },
  });

  // ── The shell ────────────────────────────────────────────────────────────
  function buildShell(): DesignerShell {
    const create: CreateShell = layout === 'phone' ? createPhoneShell : createDesktopShell;
    const built = create({
      ctx,
      canvas,
      defs,
      actions: {
        undo: () => dispatch({ type: 'undo' }),
        redo: () => dispatch({ type: 'redo' }),
        canUndo: () => store.state().past.length > 0,
        canRedo: () => store.state().future.length > 0,
        done: () => void finish(),
        cancel: () => options.onCancel?.(),
        // One price for the header chip, the Pregled column and the phone's
        // top bar — the store's, which already skips the authored layers.
        price: () => {
          const price = store.meta().price;
          return price > 0 ? options.currency.format(price) : null;
        },
        problems,
        explainFirstProblem,
        onSaved: (listener) => {
          savedListeners.add(listener);
          return () => savedListeners.delete(listener);
        },
        ...(options.previewProduct ? { previewProduct: options.previewProduct } : {}),
      },
      ...(options.surfaces ? { surfaces: options.surfaces } : {}),
      ...(options.productOptions ? { productOptions: options.productOptions } : {}),
      ...(options.switchSurface
        ? {
          switchSurface: (id: string) => {
            if (explainFirstProblem()) return;
            store.flush();
            options.switchSurface!(id, store.value());
          },
        }
        : {}),
    });
    root.append(built.element);
    root.classList.toggle('is-phone', built.layout === 'phone');
    return built;
  }

  shell = buildShell();
  shell?.sync();

  /**
   * A window dragged across 860 px, or a tablet rotated, changes which chrome
   * is right. Rebuilding the shell is cheap (the store, the canvas and every
   * cache survive it) and far safer than one set of nodes trying to be both.
   */
  function checkLayout(): void {
    const next = layoutFor(options.layout, root.clientWidth || options.host.clientWidth || 0, coarse);
    if (next === layout) return;
    layout = next;
    const open = shell?.current();
    shell?.destroy();
    shell = buildShell();
    if (open) shell?.open(open);
    canvas.relayout();
  }

  // ── Finishing ────────────────────────────────────────────────────────────

  /**
   * "Gotovo" does not close on a design the commit would refuse. The codes are
   * the same list the API validates with, so the shopper reads the reason here
   * rather than discovering it at the till.
   */
  function explainFirstProblem(): boolean {
    const blocking = problems().filter(isBlocking);
    const first = blocking[0];
    if (!first) return false;
    ctx.toast(problemMessage(first), 'error');
    if (first.layerId) {
      const layer = store.state().answer.layers.find((item) => item.id === first.layerId);
      if (layer) {
        hidden.delete(layer.id);
        dispatch({ type: 'select', id: layer.id });
        shell?.open(toolFor(layer));
      }
    }
    return true;
  }

  async function finish(): Promise<void> {
    if (explainFirstProblem()) return;
    if (!isEmptyDesign(store.value())) {
      const value = store.value();
      try {
        const scene = zoneScene(options.config, value, Math.min(3, 800 / cfg.sizeMm.w));
        const rendered = await options.renderer.renderRaw?.(scene, 1);
        if (destroyed) return;
        if (!rendered) throw new Error('Sačekajte da se učita prikaz za štampu.');
        if (value !== store.value()) throw new Error('Dizajn je promenjen. Ponovo proverite prikaz za štampu.');
      } catch (error) {
        ctx.toast(error instanceof Error ? error.message : 'Proverite prikaz za štampu.', 'error');
        return;
      }
    }
    // Anything hidden "while I work" comes back: the design that leaves is the
    // whole list, and the card behind must show it that way.
    hidden.clear();
    canvas.sync();
    store.flush();
    options.onDone(store.value());
  }

  // ── Reacting to the store ────────────────────────────────────────────────
  let lastSelected: string | null = store.state().selectedId;
  const unsubscribe = store.subscribe((state: DesignerState, previous: DesignerState) => {
    if (state.answer !== previous.answer) {
      problemCache = undefined;
      markDirty();
      for (const id of ctx.selection) {
        if (!state.answer.layers.some((layer) => layer.id === id)) ctx.selection.delete(id);
      }
    }
    canvas.sync();
    shell?.sync();
    if (state.selectedId !== lastSelected) {
      lastSelected = state.selectedId;
      options.onSelect?.(state.selectedId);
      shell?.refresh();
      return;
    }
    if (state.answer === previous.answer) return;
    if (state.tag && LIVE_TAGS.test(state.tag)) return;
    shell?.refresh();
  });

  // ── Focus, sizing, mount ─────────────────────────────────────────────────
  function focusables(): HTMLElement[] {
    const list = root.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    return [...list].filter((node) => !node.hidden && node.offsetParent !== null);
  }

  root.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;
    const nodes = focusables();
    if (nodes.length < 2) return;
    const first = nodes[0]!;
    const last = nodes[nodes.length - 1]!;
    const owner = root.getRootNode();
    const active = owner instanceof ShadowRoot ? owner.activeElement : document.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  });

  options.host.appendChild(root);
  // The overlay's own height comes from the visual viewport, never 100vh: an
  // iOS toolbar or an open keyboard would otherwise hide the Gotovo button.
  const stopViewport = watchViewport(root);
  const refit = (): void => {
    checkLayout();
    shell?.relayout();
    canvas.relayout();
    shell?.sync();
  };
  const stopSize = observeSize(root, refit, 90);
  /**
   * ResizeObserver is delivered on the rendering lifecycle, which a hidden or
   * minimised document does not run — the same trap that stops
   * `requestAnimationFrame` (see guide.ts). The window's own resize event does
   * fire, so it is kept as a second path: a designer that was opened while the
   * tab was in the background must not come back to a mis-fitted zone.
   */
  let refitTimer: number | undefined;
  const onWindowResize = (): void => {
    window.clearTimeout(refitTimer);
    refitTimer = window.setTimeout(refit, 80);
  };
  window.addEventListener('resize', onWindowResize);
  window.addEventListener('orientationchange', onWindowResize);
  canvas.relayout();
  shell?.sync();
  try {
    root.focus({ preventScroll: true });
  } catch {
    /* older engines take no options */
  }

  return {
    element: root,
    getValue: () => store.value(),
    setValue: (value) => {
      hidden.clear();
      problemCache = undefined;
      const raw = value ?? emptyAnswer();
      const base = options.config.sticker?.required ? { ...raw, sticker: raw.sticker ?? shopperStickerSettings(options.config) } : raw;
      const restored = base.sticker?.imageBorder === undefined && base.sticker
        ? applyDesignerCommand(options.config, base, { op: 'sticker', settings: { ...base.sticker, imageBorder: options.config.sticker?.defaults?.imageBorder ?? false } }) : base;
      store.dispatch({
        type: 'set',
        answer: options.config.sticker?.required
          ? { ...restored, sticker: restored.sticker ?? shopperStickerSettings(options.config) }
          : restored,
        history: false,
        resetHistory: true,
        select: null,
      });
      canvas.sync();
      shell?.refresh();
    },
    relayout: refit,
    setProductBackdrop: (backdrop) => canvas.setProductBackdrop(backdrop),
    selectLayer: (layerId) => dispatch({ type: 'select', id: layerId }),
    addText: (text) => {
      addTextLayer(ctx, text);
      shell?.open('text');
    },
    destroy: () => {
      destroyed = true;
      closeOverlays(ctx);
      unsubscribe();
      stopSize();
      window.clearTimeout(refitTimer);
      window.clearTimeout(savedTimer);
      window.removeEventListener('resize', onWindowResize);
      window.removeEventListener('orientationchange', onWindowResize);
      stopViewport();
      canvas.destroy();
      shell?.destroy();
      store.destroy();
      root.remove();
    },
  };
};
